/**
 * @module stores/libraryStore
 * @description Library state management
 *
 * Manages library state including:
 * - User library
 * - Group libraries
 * - Library selection
 * - Library loading state
 *
 * @example
 * import { libraryStore, selectedLibrary, selectLibrary } from '@stores/libraryStore';
 *
 * // Get current library
 * const library = $selectedLibrary;
 *
 * // Select a library
 * selectLibrary({ id: 123, type: 'user', name: 'My Library' });
 *
 * // Load groups
 * await loadGroups(userId);
 */

import { writable, derived, get } from 'svelte/store';
import { createLogger } from '@services/loggerService';
import { notify } from '@services/notificationService';
import { indexedDBService } from '@services/indexedDBService';
import { zoteroService } from '@services/zoteroService';
import { translate } from '@/i18n';
import type { Library, LibraryType, ZoteroGroup, StoredLibrary } from '@types';

const log = createLogger('store');

// =============================================================================
// TYPES
// =============================================================================

/** Library state */
export interface LibraryState {
  /** User's personal library */
  userLibrary: Library | null;
  /** Group libraries the user has access to */
  groups: ZoteroGroup[];
  /** Currently selected library */
  selectedLibrary: StoredLibrary | null;
  /** Whether libraries are loading */
  isLoading: boolean;
  /** Error message */
  error: string | null;
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: LibraryState = {
  userLibrary: null,
  groups: [],
  selectedLibrary: null,
  isLoading: false,
  error: null,
};

// =============================================================================
// STORE
// =============================================================================

/** Internal writable store */
const store = writable<LibraryState>(initialState);

/** Exported library store (read-only subscribe) */
export const libraryStore = {
  subscribe: store.subscribe,
};

// =============================================================================
// DERIVED STORES
// =============================================================================

/** Currently selected library */
export const selectedLibrary = derived(store, ($store) => $store.selectedLibrary);

/** Selected library ID */
export const selectedLibraryId = derived(store, ($store) => $store.selectedLibrary?.id ?? null);

/** Selected library type */
export const selectedLibraryType = derived(store, ($store) => $store.selectedLibrary?.type ?? null);

/** User's personal library */
export const userLibrary = derived(store, ($store) => $store.userLibrary);

/** Available group libraries */
export const groupLibraries = derived(store, ($store) => $store.groups);

/** All available libraries (user + groups) */
export const allLibraries = derived(store, ($store) => {
  const libraries: StoredLibrary[] = [];

  // Add user library
  if ($store.userLibrary) {
    libraries.push({
      id: $store.userLibrary.id,
      type: 'user',
      name: $store.userLibrary.name,
    });
  }

  // Add group libraries
  for (const group of $store.groups) {
    libraries.push({
      id: group.id,
      type: 'group',
      name: group.data.name,
    });
  }

  return libraries;
});

/** Whether libraries are loading */
export const isLibraryLoading = derived(store, ($store) => $store.isLoading);

/** Library error */
export const libraryError = derived(store, ($store) => $store.error);

/** Whether a library is selected */
export const hasSelectedLibrary = derived(store, ($store) => $store.selectedLibrary !== null);

// =============================================================================
// ACTIONS
// =============================================================================

/**
 * Initialize library store from storage
 */
export function initLibraryStore(userId: number): void {
  log.info('Initializing library store', { userId });

  // Set user library
  const userLib: Library = {
    id: userId,
    name: translate('library.myLibrary'),
    type: 'user',
    collections: [],
  };

  store.update((s) => ({
    ...s,
    userLibrary: userLib,
  }));

  // Restore selected library from storage
  const stored = indexedDBService.getSelectedLibrary();
  if (stored) {
    log.debug('Restored selected library', { ...stored });
    store.update((s) => ({
      ...s,
      selectedLibrary: stored,
    }));
  } else {
    // Default to user library
    const defaultLib: StoredLibrary = {
      id: userId,
      type: 'user',
      name: translate('library.myLibrary'),
    };
    store.update((s) => ({
      ...s,
      selectedLibrary: defaultLib,
    }));
    indexedDBService.setSelectedLibrary(defaultLib);
  }
}

/**
 * Load user's group libraries
 */
export async function loadGroups(userId: number): Promise<void> {
  log.info('Loading groups', { userId });
  store.update((s) => ({ ...s, isLoading: true, error: null }));

  const result = await zoteroService.getGroups(userId);

  if (result.success) {
    store.update((s) => ({
      ...s,
      groups: result.data,
      isLoading: false,
    }));
    log.info('Groups loaded', { count: result.data.length });
  } else {
    store.update((s) => ({
      ...s,
      isLoading: false,
      error: result.error,
    }));
    log.error('Failed to load groups', { error: result.error });
  }
}

/**
 * Select a library
 */
export function selectLibrary(library: StoredLibrary): void {
  log.info('Selecting library', { ...library });

  store.update((s) => ({
    ...s,
    selectedLibrary: library,
  }));

  // Persist selection
  indexedDBService.setSelectedLibrary(library);
}

/**
 * Select user library
 */
export function selectUserLibrary(userId: number): void {
  selectLibrary({
    id: userId,
    type: 'user',
    name: translate('library.myLibrary'),
  });
}

/**
 * Select a group library
 */
export function selectGroupLibrary(group: ZoteroGroup): void {
  selectLibrary({
    id: group.id,
    type: 'group',
    name: group.data.name,
  });
}

/**
 * Clear library selection
 */
export function clearLibrarySelection(): void {
  log.debug('Clearing library selection');
  store.update((s) => ({
    ...s,
    selectedLibrary: null,
  }));
}

/**
 * Clear library error
 */
export function clearLibraryError(): void {
  store.update((s) => ({ ...s, error: null }));
}

/**
 * Get current library state
 */
export function getLibraryState(): LibraryState {
  return get(store);
}

/**
 * Get library by ID and type
 */
export function getLibraryById(id: number, type: LibraryType): StoredLibrary | null {
  const state = get(store);

  if (type === 'user' && state.userLibrary?.id === id) {
    return {
      id: state.userLibrary.id,
      type: 'user',
      name: state.userLibrary.name,
    };
  }

  if (type === 'group') {
    const group = state.groups.find((g) => g.id === id);
    if (group) {
      return {
        id: group.id,
        type: 'group',
        name: group.data.name,
      };
    }
  }

  return null;
}

// =============================================================================
// STORE RESET
// =============================================================================

/**
 * Reset store to initial state
 */
export function resetLibraryStore(): void {
  store.set({ ...initialState });
}

export default libraryStore;
